import { ref, onUnmounted, type Ref } from 'vue'

export interface WaveformConfig {
  barWidth?: number
  gap?: number
  sensitivity?: number
  smoothingFactor?: number
  minHeight?: number
}

export const useWaveformVisualizer = (
  canvasRef: Ref<HTMLCanvasElement | null>,
  config: WaveformConfig = {},
) => {
  const { barWidth = 3, gap = 2, sensitivity = 2.0, smoothingFactor = 0.25, minHeight = 6 } = config

  const previousHeights = ref<number[]>([])
  const staticWaveformHeights = ref<number[]>([])
  const animationFrameRef = ref<number | null>(null)

  const getPrimaryColor = () => {
    return (
      getComputedStyle(document.documentElement).getPropertyValue('--p-primary-color').trim() ||
      '#8B5CF6'
    )
  }

  const setupCanvas = () => {
    if (!canvasRef.value) return null

    const canvas = canvasRef.value
    const ctx = canvas.getContext('2d')
    if (!ctx) return null

    const rect = canvas.getBoundingClientRect()
    if (canvas.width !== rect.width || canvas.height !== rect.height) {
      canvas.width = rect.width
      canvas.height = rect.height
    }

    return { canvas, ctx }
  }

  const drawLiveWaveform = (analyser: AnalyserNode, dataArray: Uint8Array<ArrayBuffer>) => {
    const setup = setupCanvas()
    if (!setup) return

    const { canvas, ctx } = setup

    analyser.getByteFrequencyData(dataArray)
    ctx.clearRect(0, 0, canvas.width, canvas.height)

    const primaryColor = getPrimaryColor()
    const totalBarWidth = barWidth + gap
    const barCount = Math.floor(canvas.width / totalBarWidth)

    if (previousHeights.value.length !== barCount) {
      previousHeights.value = Array.from({ length: barCount }, () => 8)
    }

    const dataStep = Math.floor(dataArray.length / barCount)
    const maxHeight = canvas.height - 8

    for (let i = 0; i < barCount; i++) {
      let value = 0
      const startIndex = i * dataStep
      const endIndex = Math.min(startIndex + dataStep, dataArray.length)

      for (let j = startIndex; j < endIndex; j++) {
        value += dataArray[j] || 0
      }
      value = value / (endIndex - startIndex)

      const normalized = (value / 255) * sensitivity
      const percent = Math.min(1, Math.max(0.15, normalized))
      const targetHeight = minHeight + percent * (maxHeight - minHeight)

      const prevHeight = previousHeights.value[i] || minHeight
      const barHeight = prevHeight + (targetHeight - prevHeight) * smoothingFactor
      previousHeights.value[i] = barHeight

      const time = Date.now() / 1000
      const wave = Math.sin(time * 2 + i * 0.3) * 2
      const finalHeight = Math.max(minHeight, barHeight + wave)

      const x = i * totalBarWidth
      const y = (canvas.height - finalHeight) / 2

      const opacity = 0.5 + percent * 0.5
      ctx.globalAlpha = opacity
      ctx.fillStyle = primaryColor
      ctx.fillRect(x, y, barWidth, finalHeight)
    }

    ctx.globalAlpha = 1
  }

  const generateStaticWaveform = () => {
    const setup = setupCanvas()
    if (!setup) return

    const { canvas } = setup
    const totalBarWidth = barWidth + gap
    const barCount = Math.floor(canvas.width / totalBarWidth)

    staticWaveformHeights.value = []
    for (let i = 0; i < barCount; i++) {
      staticWaveformHeights.value.push(8 + Math.random() * (canvas.height - 16))
    }

    drawStaticWaveform()
  }

  const drawStaticWaveform = () => {
    const setup = setupCanvas()
    if (!setup || staticWaveformHeights.value.length === 0) return

    const { canvas, ctx } = setup
    ctx.clearRect(0, 0, canvas.width, canvas.height)

    const primaryColor = getPrimaryColor()
    const totalBarWidth = barWidth + gap

    for (let i = 0; i < staticWaveformHeights.value.length; i++) {
      const barHeight = staticWaveformHeights.value[i]
      const x = i * totalBarWidth
      const y = (canvas.height - barHeight) / 2

      ctx.fillStyle = primaryColor
      ctx.globalAlpha = 0.7
      ctx.fillRect(x, y, barWidth, barHeight)
    }

    ctx.globalAlpha = 1
  }

  const drawPlaybackWaveform = (progress: number) => {
    const setup = setupCanvas()
    if (!setup || staticWaveformHeights.value.length === 0) return

    const { canvas, ctx } = setup
    ctx.clearRect(0, 0, canvas.width, canvas.height)

    const primaryColor = getPrimaryColor()
    const totalBarWidth = barWidth + gap
    const barCount = staticWaveformHeights.value.length
    const transitionZoneWidth = 6

    for (let i = 0; i < barCount; i++) {
      const barHeight = staticWaveformHeights.value[i]
      const x = i * totalBarWidth
      const y = (canvas.height - barHeight) / 2

      const barProgress = i / barCount
      const barEndProgress = (i + 1) / barCount

      let opacity: number

      if (barEndProgress <= progress) {
        opacity = 1.0
      } else if (barProgress >= progress) {
        opacity = 0.25
      } else {
        const progressInBar = (progress - barProgress) / (barEndProgress - barProgress)
        opacity = 0.25 + 0.75 * progressInBar
      }

      const distanceFromProgress = Math.abs(barProgress - progress) * barCount
      if (distanceFromProgress < transitionZoneWidth) {
        const fadeEffect = 1 - (distanceFromProgress / transitionZoneWidth) * 0.3
        opacity *= fadeEffect
      }

      ctx.fillStyle = primaryColor
      ctx.globalAlpha = opacity
      ctx.fillRect(x, y, barWidth, barHeight)
    }

    ctx.globalAlpha = 1
  }

  const startLiveAnimation = (analyser: AnalyserNode, dataArray: Uint8Array<ArrayBufferLike>) => {
    const animate = () => {
      drawLiveWaveform(analyser, dataArray as Uint8Array<ArrayBuffer>)
      animationFrameRef.value = requestAnimationFrame(animate)
    }
    animate()
  }

  const stopAnimation = () => {
    if (animationFrameRef.value !== null) {
      cancelAnimationFrame(animationFrameRef.value)
      animationFrameRef.value = null
    }
  }

  onUnmounted(() => {
    stopAnimation()
  })

  return {
    startLiveAnimation,
    stopAnimation,
    generateStaticWaveform,
    drawStaticWaveform,
    drawPlaybackWaveform,
    staticWaveformHeights,
  }
}
